import React from 'react';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';

import { Icon, useSettings, Utils } from 'app/main/components';
import { CurrencyTextField, NumberTextField } from 'app/main/components/fields';
import { InlineTitle } from 'app/main/modules/components/FastAddDialogSettingsManager';
import { roundDecimal } from 'app/main/components/Utils';

import { type OrdersLinesEditDialogSection } from '../OrdersLinesEditDialog';

export default function OrdersLinesEditDialogPriceSection(props: OrdersLinesEditDialogSection) {
	const { t } = useSettings();
	const intl = useIntl();

	const unitBasePriceHelperText = intl.formatMessage(
		{ id: 'orderLinesEditDialog.basePrice', defaultMessage: t('DETAILS_ORDERS_LINES_ORIGINAL_VALUE') },
		{ value: Utils.formatCurrency(props.model.orli_base_price) }
	);

	const unitDiscountHelperText = intl.formatMessage(
		{ id: 'orderLinesEditDialog.discount', defaultMessage: t('DETAILS_ORDERS_LINES_ORIGINAL_VALUE') },
		{ value: Utils.formatPercentage(props.model.orli_discount) }
	);

	function calcPriceReverse(newTotalPrice: number | null, quantity = 1) {
		if (!newTotalPrice) {
			props.sendChange('orli_base_price_custom', null);
		} else {
			const newDiscount = 100 * (1 - newTotalPrice / (props.model.orli_base_price_final * quantity || 1));
			const newDiscountRounded = roundDecimal(newDiscount, 2);

			if (newDiscountRounded >= 0) {
				props.sendChange('orli_discount_custom', newDiscountRounded);
			} else {
				const newPrice = newTotalPrice / (1 - (props.model.orli_discount * quantity || 0) / 100);
				const newPriceRounded = roundDecimal(newPrice, 2);

				props.sendMultipleChanges({
					...props.model,
					orli_quantity: props.model.orli_quantity || 1,
					orli_base_price_custom: newPriceRounded,
					orli_discount_custom: null
				});
			}
		}
	}

	return (
		<>
			<InlineTitle text="ORDERS_LINES_EDIT_DIALOG_PRICE_SECTION" />
			{/* BASE PRICE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<CurrencyTextField
					name="orli_base_price_custom"
					label={t('DETAILS_ORDERS_LINES_FIELD_BASE_PRICE')}
					value={props.model.orli_base_price_final}
					helperText={
						props.model.orli_base_price_custom >= 0 && props.model.orli_base_price !== undefined
							? unitBasePriceHelperText
							: undefined
					}
					isEdit={props.canEditPrice}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* DISCOUNT */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<NumberTextField
					name="orli_discount_custom"
					label={t('DETAILS_ORDERS_LINES_FIELD_DISCOUNT')}
					value={props.model.orli_discount_final}
					helperText={
						props.model.orli_discount_custom >= 0 && props.model.orli_discount !== undefined
							? unitDiscountHelperText
							: undefined
					}
					endAdornment={<Icon icon="percent-outline" size={20} />}
					isEdit={props.canEditPrice}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* UNIT PRICE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<CurrencyTextField
					name="orli_unit_price"
					label={t('DETAILS_ORDERS_LINES_FIELD_UNIT_PRICE')}
					value={props.model.orli_unit_price}
					isEdit={props.canEditPrice}
					sendChange={(_, newValue) => calcPriceReverse(newValue)}
				/>
			</Grid>
			{/* TOT TAX EXCL */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<CurrencyTextField
					name="orli_tot_value_tax_excl"
					label={t('DETAILS_ORDERS_LINES_FIELD_TOT_TAX_EXCL')}
					value={props.model.orli_tot_value_tax_excl}
					isEdit={props.canEditPrice}
					sendChange={(_, newValue) => calcPriceReverse(newValue, props.model.orli_quantity)}
				/>
			</Grid>
		</>
	);
}
